import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ToastContext } from "../contexts/ToastContext";

/**
 * Global toast notifications.
 *
 * They settle downward from the top edge and fade out again — nothing slides,
 * pops or shakes. Colour is used sparingly: sage when something went well,
 * neutral for anything informational, and the danger tone only for a genuine
 * system failure. There is no "warning" colour in this product, so warnings
 * render as quiet neutral notes rather than amber alarms — including the glyph.
 *
 * Timing is generous on purpose. Cognitive slowing is a core symptom of the
 * thing this product exists for, so a message waits longer than the usual three
 * seconds, waits longer still under reduced motion, and stops counting down
 * entirely while the pointer or keyboard focus is on it.
 *
 * ── Announcement ──────────────────────────────────────────────────────────
 * Two permanently-mounted, visually-hidden live regions rather than one region
 * wrapped around the visible stack. Three reasons:
 *
 *   1. A live region created in the same commit as its content is generally
 *      not announced, which is what the old wrapper did every single time.
 *   2. A genuine system failure ("Could not reach the server") was queued
 *      politely behind routine "Saved." confirmations and could be missed
 *      entirely. Errors now go to an assertive region; everything else stays
 *      polite. This is the one place in the product that gets to interrupt —
 *      calm is the house style, but silently swallowing a failure isn't calm,
 *      it's just quiet.
 *   3. Announcing off the visible container meant the close button's label was
 *      read as part of the message.
 *
 * The visible stack is left as plain DOM: still reachable and readable by
 * tabbing to it, no longer a live region, so nothing is announced twice.
 */
const DEFAULT_MS = 4500;
const REDUCED_MS = 6000;

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  // Presentation only: mirror the newest message into whichever live region
  // matches its urgency, a beat after it mounts.
  const [politeSaid, setPoliteSaid] = useState("");
  const [urgentSaid, setUrgentSaid] = useState("");
  const spokenRef = useRef(0);

  useEffect(() => {
    const newest = toasts[toasts.length - 1];
    if (!newest || newest.id <= spokenRef.current) return undefined;
    spokenRef.current = newest.id;
    const { message, type } = newest;
    const t = setTimeout(() => {
      if (type === "error") setUrgentSaid(message);
      else setPoliteSaid(message);
    }, 150);
    return () => clearTimeout(t);
  }, [toasts]);

  const addToast = useCallback((message, type = "success", duration) => {
    const id = ++counterRef.current;
    setToasts((prev) => [...prev.slice(-4), { id, message, type, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const api = useMemo(() => ({
    success: (msg, dur) => addToast(msg, "success", dur),
    error: (msg, dur) => addToast(msg, "error", dur ?? 6000),
    warning: (msg, dur) => addToast(msg, "warning", dur),
    info: (msg, dur) => addToast(msg, "info", dur),
  }), [addToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <p className="sr-only" role="status" aria-live="polite">{politeSaid}</p>
      <p className="sr-only" role="alert" aria-live="assertive">{urgentSaid}</p>
      <div className="shell-toast-container">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const INFO_GLYPH = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="11.5" x2="12" y2="16" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ICONS = {
  success: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  error: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="12.5" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  // A "warning" here is a neutral note, so it takes the info glyph rather than
  // an exclamation mark. Nothing in this product alerts at the person.
  warning: INFO_GLYPH,
  info: INFO_GLYPH,
};

function ToastItem({ toast, onDismiss }) {
  const [state, setState] = useState("entering");
  const timerRef = useRef(null);
  const remainingRef = useRef(null);
  const startedAtRef = useRef(0);
  const doneRef = useRef(false);

  const dismiss = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    clearTimeout(timerRef.current);
    setState("exiting");
    setTimeout(() => onDismiss(toast.id), 300);
  }, [onDismiss, toast.id]);

  const resume = useCallback(() => {
    if (doneRef.current || remainingRef.current === null) return;
    startedAtRef.current = Date.now();
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(dismiss, remainingRef.current);
  }, [dismiss]);

  const pause = useCallback(() => {
    if (doneRef.current || remainingRef.current === null) return;
    clearTimeout(timerRef.current);
    remainingRef.current = Math.max(
      1200,
      remainingRef.current - (Date.now() - startedAtRef.current),
    );
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => setState("visible"));

    const base = toast.duration ?? DEFAULT_MS;
    remainingRef.current = prefersReducedMotion() ? Math.max(base, REDUCED_MS) : base;
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remainingRef.current);

    return () => clearTimeout(timerRef.current);
  }, [toast.id, toast.duration, dismiss]);

  return (
    <div
      className={`shell-toast shell-toast--${toast.type} shell-toast--${state}`}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <span className="shell-toast-icon" aria-hidden="true">{ICONS[toast.type]}</span>
      <span className="shell-toast-message">{toast.message}</span>
      <button
        type="button"
        className="shell-toast-close"
        // Up to five toasts can stack, and five buttons all called "Dismiss"
        // are indistinguishable to anyone tabbing the stack.
        aria-label={`Dismiss: ${toast.message}`}
        onClick={dismiss}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}
